/**
 * Lädt den Mailverlauf eines Kontakts SCHRITTWEISE (User 02.11.2026: „Laden
 * dauert zu lange").
 *
 * Bis dahin wartete der Reiter auf drei Quellen zugleich (`Promise.all`):
 * die neuesten 200 Mails des Posteingangs, die neuesten 100 des Archivs — beide
 * vollständig samt Kontakterkennung über den Server gezogen, nur um sie danach
 * im Browser auf den einen Kontakt zu sieben — und die Verknüpfungen. Erst
 * wenn die langsamste Quelle fertig war, stand irgendetwas da.
 *
 * Jetzt:
 *  - Die Verknüpfungen (Datenbank, schnell) melden sich, sobald sie da sind.
 *  - Je Postfach EINE gezielte Graph-Suche nach den Adressen des Kontakts
 *    (`participants:`), höchstens {@link PARALLEL} Postfächer gleichzeitig;
 *    jedes meldet sein Ergebnis einzeln.
 *  - Fällt eine Quelle aus, bleibt der Rest sichtbar und die ausgefallene Quelle
 *    wird benannt (`onFehler`) — keine stille Teilansicht.
 */

export const PARALLEL = 3;

export type KommQuelle = { art: "verknuepfte" } | { art: "postfach"; mailbox: string };

export type KontaktKommunikationLader<TMail, TLink> = {
    postfaecher: string[];
    /** KQL-Anfragen; leer = der Kontakt hat keine Adresse → keine Graph-Suche. */
    abfragen: string[];
    ladeVerknuepfte: (() => Promise<TLink[]>) | null;
    suche: (mailbox: string, kql: string) => Promise<TMail[]>;
    onVerknuepfte: (links: TLink[]) => void;
    onPostfach: (mailbox: string, mails: TMail[]) => void;
    onFehler: (quelle: KommQuelle, error: unknown) => void;
    /** true = Ergebnis verwerfen (Kontaktwechsel/Unmount). */
    abgebrochen?: () => boolean;
    parallel?: number;
};

export async function ladeKontaktKommunikation<TMail, TLink>(l: KontaktKommunikationLader<TMail, TLink>): Promise<void> {
    const weg = () => l.abgebrochen?.() === true;

    const verknuepfte = (async () => {
        if (!l.ladeVerknuepfte) return;
        try {
            const links = await l.ladeVerknuepfte();
            if (!weg()) l.onVerknuepfte(links);
        } catch (error) {
            if (!weg()) l.onFehler({ art: "verknuepfte" }, error);
        }
    })();

    const warteschlange = l.abfragen.length > 0 ? [...l.postfaecher] : [];
    const arbeiter = async () => {
        for (let mailbox = warteschlange.shift(); mailbox !== undefined; mailbox = warteschlange.shift()) {
            if (weg()) return;
            const mb = mailbox;
            try {
                const teile = await Promise.all(l.abfragen.map((kql) => l.suche(mb, kql)));
                if (!weg()) l.onPostfach(mb, teile.flat());
            } catch (error) {
                if (!weg()) l.onFehler({ art: "postfach", mailbox: mb }, error);
            }
        }
    };
    const anzahl = warteschlange.length ? Math.max(1, Math.min(l.parallel ?? PARALLEL, warteschlange.length)) : 0;
    await Promise.all([verknuepfte, ...Array.from({ length: anzahl }, arbeiter)]);
}
